/**
 * CDB-TQS video ingest — the one typed client for every contract of the video
 * plan (cdb-knowledge-mobile `docs/VIDEO-INGEST-PLAN.md`, TQS-1…TQS-4).
 *
 * The TQS routes are built in parallel to this client. Everything the backend
 * says is parsed defensively here, and a missing route (404/405/422/501) is
 * reported as "not available" instead of an error, so callers fall back to the
 * behaviour from before the video plan. Keep every video field and route in
 * this file: a contract change then touches one module and its test.
 */

import { logApiCall, logApiResponse } from '@/lib/logger'
import { CDB_TQS_API_BASE_URL, getAuthHeaders } from './http'
// Type-only: documents.ts imports this module, so its classes are loaded
// lazily in throwUploadError to keep the module graph free of cycles.
import type { ExistingDocumentInfo } from './documents'

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export interface TqsResult<T = unknown> {
  status: number
  ok: boolean
  data: T | null
  /** Response headers (e.g. Retry-After of a 429). */
  headers?: Headers
}

/** Statuses meaning "this backend does not offer the route (yet)". */
const ROUTE_MISSING = new Set([404, 405, 422, 501])

export function isRouteMissing(status: number): boolean {
  return ROUTE_MISSING.has(status)
}

interface TqsRequestInit {
  method?: string
  /** JSON body; a Blob/ArrayBuffer is sent as is (octet-stream). */
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

/** `fetch` with the session's Bearer; a 401 refreshes the token and retries once. */
async function fetchWithBearer(url: string, init: RequestInit & { headers?: Record<string, string> }): Promise<Response> {
  const send = (auth: Record<string, string>) =>
    fetch(url, { ...init, credentials: 'include', headers: { ...init.headers, ...auth } })

  let response = await send(await getAuthHeaders())
  if (response.status === 401) {
    const { authService } = await import('@/services/auth-service')
    const token = await authService.refreshAccessToken()
    if (token) response = await send({ Authorization: `Bearer ${token}` })
  }
  return response
}

/**
 * One request against `/api/v1/tqs/<path>` that never throws on an HTTP
 * status — callers branch on it. Network errors and aborts do throw.
 * A 401 refreshes the token and retries once, like `fetchWithLogging`.
 */
export async function tqsRequest<T = unknown>(path: string, init: TqsRequestInit = {}): Promise<TqsResult<T>> {
  const url = `${CDB_TQS_API_BASE_URL}/${path.replace(/^\/+/, '')}`
  const method = init.method ?? 'GET'
  const isBinary = init.body instanceof Blob || init.body instanceof ArrayBuffer
  const body =
    init.body === undefined ? undefined : isBinary ? (init.body as BodyInit) : JSON.stringify(init.body)
  const contentType: Record<string, string> =
    init.body === undefined ? {} : { 'Content-Type': isBinary ? 'application/octet-stream' : 'application/json' }

  logApiCall(method, url)
  const response = await fetchWithBearer(url, {
    method,
    headers: { ...contentType, ...init.headers },
    body,
    signal: init.signal,
  })

  let data: unknown = null
  if (response.status !== 204) {
    try {
      data = (response.headers.get('content-type') ?? '').includes('application/json')
        ? await response.json()
        : null
    } catch {
      data = null
    }
  }
  logApiResponse(method, url, response.status, data)
  return { status: response.status, ok: response.ok, data: data as T | null, headers: response.headers }
}

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/** `{detail: {...}}` or the bare object → the detail record. */
export function errorDetail(data: unknown): Record<string, unknown> | null {
  const outer = record(data)
  if (!outer) return null
  return record(outer.detail) ?? outer
}

// ---------------------------------------------------------------------------
// Document fields (TQS-1: DocumentResponse.media_type "video" + `video`)
// ---------------------------------------------------------------------------

export type DocumentMediaType = 'image' | 'document' | 'video'

export function parseMediaType(value: unknown): DocumentMediaType | undefined {
  return value === 'image' || value === 'document' || value === 'video' ? value : undefined
}

export interface VideoChapter {
  start_s: number
  title: string
}

/** `DocumentResponse.video` — facts about a processed video (TQS-1/TQS-3). */
export interface DocumentVideoInfo {
  duration_s?: number
  width?: number
  height?: number
  has_audio?: boolean
  /** Number of distinct speakers found by the transcription. */
  speakers?: number
  keyframes?: number
  /** AI-written summary ("Einordnung") of the whole video. */
  summary?: string
  codec?: string
  /** Server hint whether browsers can usually play the codec (HEVC/AV1 cannot everywhere). */
  playable_hint?: boolean
  chapters?: VideoChapter[]
}

export function parseDocumentVideoInfo(value: unknown): DocumentVideoInfo | null {
  const raw = record(value)
  if (!raw) return null
  const chapters = Array.isArray(raw.chapters)
    ? raw.chapters
        .map((c) => record(c))
        .filter((c): c is Record<string, unknown> => !!c && num(c.start_s) !== undefined && !!str(c.title))
        .map((c) => ({ start_s: num(c.start_s)!, title: str(c.title)! }))
    : undefined
  return {
    duration_s: num(raw.duration_s),
    width: num(raw.width),
    height: num(raw.height),
    has_audio: typeof raw.has_audio === 'boolean' ? raw.has_audio : undefined,
    speakers: num(raw.speakers),
    keyframes: num(raw.keyframes),
    summary: str(raw.summary),
    codec: str(raw.codec),
    playable_hint: typeof raw.playable_hint === 'boolean' ? raw.playable_hint : undefined,
    chapters: chapters && chapters.length > 0 ? chapters : undefined,
  }
}

// ---------------------------------------------------------------------------
// Retrieval reference fields (TQS-4: media_type "video", start_s, end_s, frame)
// ---------------------------------------------------------------------------

export interface VideoReferenceFields {
  start_s?: number
  end_s?: number
  /** Present when the cited chunk is a key frame's (AI) description. */
  frame?: string
}

export function parseVideoReferenceFields(ref: Record<string, unknown>): VideoReferenceFields {
  const start = num(ref.start_s)
  const end = num(ref.end_s)
  return {
    start_s: start !== undefined && start >= 0 ? start : undefined,
    end_s: end !== undefined && end >= 0 ? end : undefined,
    frame: str(ref.frame),
  }
}

// ---------------------------------------------------------------------------
// Playback (TQS-4: POST /documents/{id}/stream-token, BFF-1 public stream)
// ---------------------------------------------------------------------------

/** The only path a stream URL may point at: the BFF's public stream route. */
const STREAM_PATH = '/api/v1/tqs/documents/public/'

export interface VideoStreamToken {
  /** Absolute URL for `<video src>`, token included. */
  url: string
  expiresIn: number
  mimeType?: string
  durationS?: number
  /** WebVTT captions for `<track>`, when the backend offers them. */
  captionsUrl?: string
}

/**
 * Turn a server-given stream path into an absolute URL on the BFF origin.
 * Only same-origin paths under the public stream route are accepted — the URL
 * carries a credential and is handed straight to the media element.
 */
export function resolveStreamUrl(value: unknown): string | undefined {
  const raw = str(value)
  if (!raw) return undefined
  const origin = new URL(CDB_TQS_API_BASE_URL).origin
  let parsed: URL
  try {
    parsed = new URL(raw, origin)
  } catch {
    return undefined
  }
  if (parsed.origin !== origin || !parsed.pathname.startsWith(STREAM_PATH)) return undefined
  return parsed.toString()
}

export function parseStreamToken(data: unknown): VideoStreamToken | null {
  const raw = record(data)
  if (!raw) return null
  const url = resolveStreamUrl(raw.url)
  if (!url) return null
  return {
    url,
    expiresIn: num(raw.expires_in) ?? 900,
    mimeType: str(raw.mime_type),
    durationS: num(raw.duration_s),
    captionsUrl: resolveStreamUrl(raw.captions_url),
  }
}

/**
 * A short-lived stream URL for a Knowledge Area video, or null when the
 * backend cannot stream it (no route yet, not a video, flag off). Callers then
 * fall back to the Bearer download of `/documents/{id}/file`. 403 throws.
 */
export async function createVideoStreamToken(
  documentId: string,
  signal?: AbortSignal,
): Promise<VideoStreamToken | null> {
  const result = await tqsRequest(`documents/${encodeURIComponent(documentId)}/stream-token`, {
    method: 'POST',
    signal,
  })
  if (result.ok) return parseStreamToken(result.data)
  if (isRouteMissing(result.status) || result.status === 415) return null
  throw new Error(`stream-token failed: ${result.status}`)
}

// ---------------------------------------------------------------------------
// Formats (TQS-1/TQS-2: GET /documents/formats)
// ---------------------------------------------------------------------------

export interface VideoIngestFormats {
  videos_enabled: boolean
  /** Per-video limit (plan §9: 2 GiB). */
  max_video_size_mb: number | null
  max_video_duration_s: number | null
}

export interface ResumableUploadInfo {
  max_block_bytes: number
}

/** Hard ceiling of the contract (Cloudflare 100 MB, BFF memory): 8 MiB. */
export const MAX_BLOCK_BYTES = 8 * 1024 * 1024

/** The video part of `/documents/formats`, or undefined on a backend without it. */
export function parseVideoIngestFormats(data: unknown): VideoIngestFormats | undefined {
  const raw = record(data)
  if (!raw || typeof raw.videos_enabled !== 'boolean') return undefined
  return {
    videos_enabled: raw.videos_enabled,
    max_video_size_mb: num(raw.max_video_size_mb) ?? null,
    max_video_duration_s: num(raw.max_video_duration_s) ?? null,
  }
}

/**
 * `resumable_upload` of `/documents/formats`, or undefined unless the server
 * says `enabled: true` — with the flag off it still reports its block size.
 */
export function parseResumableUploadInfo(data: unknown): ResumableUploadInfo | undefined {
  const raw = record(record(data)?.resumable_upload)
  if (raw?.enabled !== true) return undefined
  const block = num(raw.max_block_bytes)
  if (!block || block <= 0) return undefined
  return { max_block_bytes: Math.min(block, MAX_BLOCK_BYTES) }
}

// ---------------------------------------------------------------------------
// Resumable block upload (TQS-2: /documents/uploads)
// ---------------------------------------------------------------------------

export interface UploadSessionRequest {
  filename: string
  size_bytes: number
  /** Hex SHA-256 of the whole file (required). */
  sha256: string
  knowledge_area: string
  client_id?: string
  title?: string
  tags?: string[]
  origin?: 'human' | 'ai_generated'
  drive_file_id?: string
  drive_version?: number
  /** The document this upload is the new version of (cdb_tqs#1974). */
  version_of?: string
}

export interface ReceivedBlock {
  index: number
  size: number
}

export interface UploadSession {
  upload_id: string
  document_id: string | null
  max_block_bytes: number
  expires_at: string | null
  received: ReceivedBlock[]
}

export type UploadSessionStateName = 'open' | 'completing' | 'completed' | 'aborted'

export interface UploadSessionState {
  state: UploadSessionStateName
  size_bytes: number | null
  received: ReceivedBlock[]
  received_bytes: number
  document_id: string | null
  job_id: string | null
}

/** 202 of `/uploads/{id}/complete` — the same JobResponse as `/documents/upload`. */
export interface UploadCompleteResponse {
  job_id?: string
  document_id?: string
  status?: string
  message?: string
}

/** The backend offers no block upload (route missing or flag off). */
export class ResumableUploadUnavailableError extends Error {
  constructor() {
    super('Resumable upload is not available on this server')
    this.name = 'ResumableUploadUnavailableError'
  }
}

/** 413 `file_too_large` / `block_too_large`: final for this file. */
export class UploadTooLargeError extends Error {
  readonly maxBytes: number | null
  readonly mediaType: string | null

  constructor(maxBytes: number | null, mediaType: string | null) {
    super(`Upload too large${maxBytes ? ` (max ${maxBytes} bytes)` : ''}`)
    this.name = 'UploadTooLargeError'
    this.maxBytes = maxBytes
    this.mediaType = mediaType
  }
}

/** Any other refusal of an upload session, with the server's code. */
export class UploadSessionError extends Error {
  readonly status: number
  readonly code: string | null
  readonly missing: number[]
  /** The server's own text (German), only a fallback for display. */
  readonly serverMessage: string | null

  constructor(status: number, code: string | null, missing: number[] = [], serverMessage: string | null = null) {
    super(`Upload session error ${status}${code ? ` ${code}` : ''}`)
    this.name = 'UploadSessionError'
    this.status = status
    this.code = code
    this.missing = missing
    this.serverMessage = serverMessage
  }
}

/** `Retry-After` (seconds or HTTP date) in milliseconds, or undefined. */
export function parseRetryAfterMs(value: string | null | undefined, now: number = Date.now()): number | undefined {
  if (!value) return undefined
  const seconds = Number(value.trim())
  if (Number.isFinite(seconds)) return seconds >= 0 ? seconds * 1000 : undefined
  const date = Date.parse(value)
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined
}

/** A 404/405/501 of `POST /documents/uploads` that means "no such route" — not a refusal. */
function isUploadRouteMissing(result: TqsResult): boolean {
  if (result.status === 405 || result.status === 501) return true
  if (result.status !== 404) return false
  const outer = record(result.data)
  // FastAPI's own 404 for an unknown path: {"detail": "Not Found"} (or no body).
  if (!outer || outer.detail === undefined || outer.detail === 'Not Found') return true
  return str(errorDetail(result.data)?.code) === 'resumable_upload_disabled'
}

function receivedList(value: unknown): ReceivedBlock[] {
  if (!Array.isArray(value)) return []
  return value
    .map((b) => record(b))
    .filter((b): b is Record<string, unknown> => !!b && num(b.index) !== undefined && num(b.size) !== undefined)
    .map((b) => ({ index: num(b.index)!, size: num(b.size)! }))
}

export function parseUploadSession(data: unknown): UploadSession | null {
  const raw = record(data)
  const id = str(raw?.upload_id)
  if (!raw || !id) return null
  return {
    upload_id: id,
    document_id: str(raw.document_id) ?? null,
    max_block_bytes: Math.min(num(raw.max_block_bytes) ?? MAX_BLOCK_BYTES, MAX_BLOCK_BYTES),
    expires_at: str(raw.expires_at) ?? null,
    received: receivedList(raw.received),
  }
}

export function parseUploadSessionState(data: unknown): UploadSessionState | null {
  const raw = record(data)
  const state = raw?.state
  if (!raw || (state !== 'open' && state !== 'completing' && state !== 'completed' && state !== 'aborted')) return null
  const received = receivedList(raw.received)
  return {
    state,
    size_bytes: num(raw.size_bytes) ?? null,
    received,
    received_bytes: num(raw.received_bytes) ?? received.reduce((sum, b) => sum + b.size, 0),
    document_id: str(raw.document_id) ?? null,
    job_id: str(raw.job_id) ?? null,
  }
}

/**
 * Map a refused session call to the typed errors the upload UIs already know
 * (409 duplicate, 413, 415, 429) plus `UploadSessionError` for the rest.
 */
async function throwUploadError(result: TqsResult): Promise<never> {
  const detail = errorDetail(result.data)
  const code = str(detail?.code) ?? null
  const {
    DriveFileNotFoundError,
    DuplicateDocumentError,
    UnsupportedFormatError,
    UploadRateLimitError,
    parseUnsupportedFormatDetail,
  } = await import('./documents')
  const { parseVersionError } = await import('./documentVersions')
  const versionError = parseVersionError(result.status, result.data)
  if (versionError) throw versionError
  if (result.status === 404 && code === 'drive_file_not_found') {
    throw new DriveFileNotFoundError(str(detail?.message) ?? 'Drive file not found')
  }
  if (result.status === 409 && (code === 'duplicate' || detail?.existing_document)) {
    throw new DuplicateDocumentError(
      str(detail?.message) ?? 'This file already exists in the system',
      (record(detail?.existing_document) as ExistingDocumentInfo | null) ?? null,
    )
  }
  if (result.status === 413) {
    throw new UploadTooLargeError(num(detail?.max_bytes) ?? null, str(detail?.media_type) ?? null)
  }
  if (result.status === 415) {
    const unsupported = parseUnsupportedFormatDetail(result.data)
    if (unsupported) throw new UnsupportedFormatError(unsupported)
  }
  if (result.status === 429) {
    throw new UploadRateLimitError(
      str(detail?.message) ?? code ?? 'Upload rate limit exceeded',
      parseRetryAfterMs(result.headers?.get('retry-after')),
    )
  }
  const missing = Array.isArray(detail?.missing)
    ? (detail!.missing as unknown[]).filter((i): i is number => typeof i === 'number')
    : []
  // `detail` may be a plain string for the errors shaped "wie /upload".
  const outerDetail = record(result.data)?.detail
  const message = str(detail?.message) ?? (typeof outerDetail === 'string' ? str(outerDetail) : undefined) ?? null
  throw new UploadSessionError(result.status, code, missing, message)
}

/** `POST /documents/uploads` — checks format, size and duplicate before the first byte. */
export async function createUploadSession(
  request: UploadSessionRequest,
  signal?: AbortSignal,
): Promise<UploadSession> {
  const result = await tqsRequest('documents/uploads', { method: 'POST', body: request, signal })
  if (result.ok) {
    const session = parseUploadSession(result.data)
    if (session) return session
    throw new ResumableUploadUnavailableError()
  }
  // Only a bare 404 (unknown path), `resumable_upload_disabled`, 405 and 501
  // mean "no block upload here". A 404 naming the Knowledge Area, client or
  // Drive file is a real refusal and must reach the user.
  if (isUploadRouteMissing(result)) throw new ResumableUploadUnavailableError()
  return throwUploadError(result)
}

/** `PUT /documents/uploads/{id}/blocks/{n}` — idempotent per index. */
export async function putUploadBlock(
  uploadId: string,
  index: number,
  block: Blob,
  sha256Hex: string,
  signal?: AbortSignal,
): Promise<void> {
  const result = await tqsRequest(`documents/uploads/${encodeURIComponent(uploadId)}/blocks/${index}`, {
    method: 'PUT',
    body: block,
    headers: { 'X-Content-SHA256': sha256Hex },
    signal,
  })
  if (result.ok) return
  return throwUploadError(result)
}

/** `GET /documents/uploads/{id}` — what the server holds; null when the session is gone. */
export async function getUploadSession(uploadId: string, signal?: AbortSignal): Promise<UploadSessionState | null> {
  const result = await tqsRequest(`documents/uploads/${encodeURIComponent(uploadId)}`, { signal })
  if (result.status === 404) return null
  if (!result.ok) return throwUploadError(result)
  return parseUploadSessionState(result.data)
}

/** `POST /documents/uploads/{id}/complete` — commits the blocks; idempotent. */
export async function completeUploadSession(
  uploadId: string,
  blockCount: number,
  signal?: AbortSignal,
): Promise<UploadCompleteResponse> {
  const result = await tqsRequest<UploadCompleteResponse>(`documents/uploads/${encodeURIComponent(uploadId)}/complete`, {
    method: 'POST',
    body: { block_count: blockCount },
    signal,
  })
  if (result.ok) return result.data ?? {}
  return throwUploadError(result)
}

/** `DELETE /documents/uploads/{id}` — discards the blocks; a gone session is fine. */
export async function abortUploadSession(uploadId: string): Promise<void> {
  const result = await tqsRequest(`documents/uploads/${encodeURIComponent(uploadId)}`, { method: 'DELETE' })
  if (result.ok || result.status === 404) return
  return throwUploadError(result)
}

// ---------------------------------------------------------------------------
// Processing progress (plan §6.3: job.media_progress) and status
// ---------------------------------------------------------------------------

export type MediaProgressStep = 'probe' | 'transcription' | 'frames' | 'vision' | 'markdown' | 'indexing'

/** Finer server stage (TQS-3), e.g. `extracting_audio`, `describing_frames`. */
export type MediaProgressStage =
  | 'probing'
  | 'extracting_audio'
  | 'transcribing'
  | 'detecting_slides'
  | 'describing_frames'
  | 'building_markdown'
  | 'indexing'

export interface MediaProgress {
  step: MediaProgressStep | string
  stage?: MediaProgressStage | string
  /** 0–99, monotonic while the job runs. */
  percent?: number
  done?: number
  total?: number
}

export function parseMediaProgress(value: unknown): MediaProgress | null {
  const raw = record(value)
  const step = str(raw?.step)
  if (!raw || !step) return null
  const percent = num(raw.percent)
  return {
    step,
    stage: str(raw.stage),
    percent: percent !== undefined && percent >= 0 ? Math.min(100, percent) : undefined,
    done: num(raw.done),
    total: num(raw.total),
  }
}

// ---------------------------------------------------------------------------
// Verifier (TQS-4: knowledge-blocks media fields + locate)
// ---------------------------------------------------------------------------

export interface LocateMatch {
  start_s: number
  end_s?: number
  chunk_index?: number
  score?: number
}

/**
 * `GET /knowledge-blocks/{id}/locate?text=` — where a statement is said in
 * the video (vector search inside this one document, at most 3). A search hit,
 * not a provenance link. Null when the backend has no such route.
 */
export async function locateInDocument(
  documentId: string,
  text: string,
  knowledgeArea?: string,
  signal?: AbortSignal,
): Promise<LocateMatch[] | null> {
  const params = new URLSearchParams({ text })
  if (knowledgeArea) params.set('knowledge_area', knowledgeArea)
  const result = await tqsRequest(`knowledge-blocks/${encodeURIComponent(documentId)}/locate?${params}`, { signal })
  if (!result.ok) {
    if (isRouteMissing(result.status)) return null
    throw new Error(`locate failed: ${result.status}`)
  }
  const matches = record(result.data)?.matches
  if (!Array.isArray(matches)) return []
  return matches
    .map((m) => record(m))
    .filter((m): m is Record<string, unknown> => !!m && num(m.start_s) !== undefined)
    .map((m) => ({ start_s: num(m.start_s)!, end_s: num(m.end_s), chunk_index: num(m.chunk_index), score: num(m.score) }))
}

// ---------------------------------------------------------------------------
// Whole-file bytes: capped in memory, streamed to disk for a download
// ---------------------------------------------------------------------------

/**
 * Largest file held in memory as a Blob (playback fallback without a stream
 * token, download in a browser without the File System Access API). A 2 GiB
 * video as a Blob takes the tab down; above this the bytes stream or nothing.
 */
export const MAX_BLOB_BYTES = 100 * 1024 * 1024

/** The file is larger than the browser may hold in memory as a Blob. */
export class FileTooLargeForBrowserError extends Error {
  readonly sizeBytes: number | null
  readonly maxBytes: number

  constructor(sizeBytes: number | null, maxBytes: number) {
    super(`File too large to load in the browser (limit ${maxBytes} bytes)`)
    this.name = 'FileTooLargeForBrowserError'
    this.sizeBytes = sizeBytes
    this.maxBytes = maxBytes
  }
}

/**
 * Read a response body into a Blob, but never more than `maxBytes`: a larger
 * `Content-Length` is refused before the first byte, a body without one is cut
 * off as soon as it passes the limit.
 */
export async function readBlobCapped(response: Response, maxBytes: number, type?: string): Promise<Blob> {
  const blobType = type ?? response.headers.get('content-type') ?? ''
  const declared = Number(response.headers.get('content-length'))
  if (Number.isFinite(declared) && declared > maxBytes) {
    await response.body?.cancel().catch(() => undefined)
    throw new FileTooLargeForBrowserError(declared, maxBytes)
  }
  if (!response.body) return new Blob([await response.arrayBuffer()], { type: blobType })

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    received += value.byteLength
    if (received > maxBytes) {
      await reader.cancel().catch(() => undefined)
      throw new FileTooLargeForBrowserError(null, maxBytes)
    }
    chunks.push(value)
  }
  return new Blob(chunks as BlobPart[], { type: blobType })
}

/** The parts of the File System Access API a streamed download needs. */
interface SaveFileHandle {
  createWritable(): Promise<WritableStream<Uint8Array>>
}
type SaveFilePicker = (options?: { suggestedName?: string }) => Promise<SaveFileHandle>

function saveFilePicker(): SaveFilePicker | null {
  const picker = (globalThis as unknown as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker
  return typeof picker === 'function' ? picker.bind(globalThis) : null
}

export type DownloadResult = 'saved' | 'cancelled'

/**
 * Download the original of a Knowledge Area document without holding it in
 * memory. Where the browser can write a file (File System Access API:
 * Chrome, Edge) the Bearer response streams straight to disk, 2 GiB videos
 * included. Elsewhere it falls back to a Blob, refused above
 * `MAX_BLOB_BYTES` with `FileTooLargeForBrowserError`.
 *
 * Call it directly from the click handler: the save dialog needs the user's
 * gesture, so nothing may be awaited before it.
 */
export async function downloadDocumentFile(
  documentId: string,
  fileName: string,
  signal?: AbortSignal,
): Promise<DownloadResult> {
  const picker = saveFilePicker()
  let target: WritableStream<Uint8Array> | null = null
  if (picker) {
    try {
      target = await (await picker({ suggestedName: fileName })).createWritable()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'
      throw error
    }
  }

  const url = `${CDB_TQS_API_BASE_URL}/documents/${encodeURIComponent(documentId)}/file`
  logApiCall('GET', url)
  let response: Response
  try {
    response = await fetchWithBearer(url, { method: 'GET', headers: { Accept: 'application/octet-stream' }, signal })
  } catch (error) {
    await target?.abort().catch(() => undefined)
    throw error
  }
  logApiResponse('GET', url, response.status, { fileName })
  if (!response.ok) {
    await response.body?.cancel().catch(() => undefined)
    await target?.abort().catch(() => undefined)
    throw new Error(`Failed to download document: ${response.status}`)
  }

  if (target) {
    if (!response.body) {
      await target.abort().catch(() => undefined)
      throw new Error('Download has no body')
    }
    await response.body.pipeTo(target, { signal })
    return 'saved'
  }

  const blob = await readBlobCapped(response, MAX_BLOB_BYTES)
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  return 'saved'
}
